import React, { useEffect, useRef, useState } from 'react';

const DEFAULT_AVATAR = '/default-avatar.png';

function normalizeAvatarSrc(src, fallbackSrc) {
  return typeof src === 'string' && src.trim() ? src.trim() : fallbackSrc;
}

export default function StableAvatar({
  src,
  fallbackSrc = DEFAULT_AVATAR,
  identityKey,
  alt = '',
  onError,
  ...imgProps
}) {
  const [displaySrc, setDisplaySrc] = useState(fallbackSrc);
  const displaySrcRef = useRef(displaySrc);
  const identityKeyRef = useRef(identityKey);

  useEffect(() => {
    displaySrcRef.current = displaySrc;
  }, [displaySrc]);

  useEffect(() => {
    if (identityKeyRef.current === identityKey) {
      return;
    }

    identityKeyRef.current = identityKey;
    setDisplaySrc(fallbackSrc);
  }, [fallbackSrc, identityKey]);

  useEffect(() => {
    const candidateSrc = normalizeAvatarSrc(src, fallbackSrc);

    if (candidateSrc === fallbackSrc || candidateSrc === displaySrcRef.current) {
      return undefined;
    }

    let cancelled = false;
    const preloadIdentityKey = identityKeyRef.current;
    const image = new Image();

    image.onload = () => {
      if (cancelled || identityKeyRef.current !== preloadIdentityKey) {
        return;
      }
      setDisplaySrc(candidateSrc);
    };

    image.onerror = () => {
      // Keep the current avatar visible. A failed refreshed URL should not
      // replace an already displayed image with the default avatar.
    };

    image.src = candidateSrc;

    return () => {
      cancelled = true;
      image.onload = null;
      image.onerror = null;
    };
  }, [fallbackSrc, identityKey, src]);

  return (
    <img
      {...imgProps}
      src={displaySrc}
      alt={alt}
      onError={(event) => {
        onError?.(event);
        if (!displaySrcRef.current || displaySrcRef.current === fallbackSrc) {
          setDisplaySrc(fallbackSrc);
        }
      }}
    />
  );
}
